import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';
import { AccountBeneficiariesDTO } from '../../../Profile/Accounts/api/useAccountsQuery';

export enum InsuranceType {
  Life,
  Disability,
  LongTermCare,
  HealthInsurance,
  PropertyAndCasualty,
}

export enum DisabilityInsurancePolicyType {
  ShortTerm,
  LongTerm,
}

export enum LifeInsurancePolicyTypeEnum {
  Term,
  Permanent,
}

export enum LifeInsurancePermanentTypeEnum {
  WholeLife,
  UniversalLife,
  GuaranteedUniversalLife,
  VariableLife,
}

export enum InsurancePeriodType {
  Day,
  Week,
  Month,
  Year,
  Age,
  Lifetime,
}

export enum InsuranceCoverageType {
  Group,
  Individual,
}

export enum HealthPlanTypeEnum {
  HMO,
  PPO,
  EPO,
  POS,
  HDHP,
  Other,
}

export interface InsurancePeriod {
  DisplayString: string;
  Label: string;
  PeriodType: InsurancePeriodType;
  Value: number;
}

interface AvailableAssetBucketDTO {
  AssetTypes: number[];
  Description: string;
  Title: string;
}

export enum PropertyTypeDTO {
  Umbrella,
  ResidentialProperty,
  CommercialProperty,
  PersonalProperty,
  Vehicles,
  BusinessPersonalProperty,
}

export enum PropertyPremiumFrequencyDTO {
  Monthly,
  Annually,
}

export interface PropertyAndCasualtyDTO {
  PropertyTypes: Array<PropertyTypeDTO>;
  CoverageStartYear: number;
  CoverageEndYear: number | null;
  Coverage: number;
  PremiumPaymentFrequency: PropertyPremiumFrequencyDTO;
  UseInflation: boolean;
  Comment: string;
  CasualtyCoverageLimit: number | null;
  CasualtyIsLiability: boolean;
}

export interface OwnerModelDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
  Percentage: number;
}

export interface InsuranceAccountDTO {
  AccountId: string;
  AccountBeneficiaries: AccountBeneficiariesDTO | null;
  InsuranceType: InsuranceType;
  Description: string;
  AccountOwner: string;
  AccountName: string;
  LifeInsurancePolicyAmount: number | null;
  InsuranceExpirationYear: string;
  NonSpecificPositionsValue: number;
  Premium: number;
  DisabilityInsurancePolicyType: DisabilityInsurancePolicyType;
  DisabilityMonthlyBenefitAmount: number | null;
  DisabilityEliminationPeriod: InsurancePeriod | null;
  HealthInsuredMembers: string[] | null;
  HealthFamilyDeductible: number | null;
  HealthOutOfPocketLimit: number | null;
  LongTermCareDailyBenefitAmount: number | null;
  LongTermCareCoveragePeriod: InsurancePeriod | null;
  LongTermCareEliminationPeriod: InsurancePeriod | null;
  IsBrokerOfRecord: boolean | null;
  InstitutionName: string;
  InstitutionId: string | null;
  InsuranceCoverageType: InsuranceCoverageType | null;
  IsPreTaxPremium: boolean;
  DisabilityCoveragePeriod: InsurancePeriod | null;
  LongTermCareHomeCareCoveragePercentage: number | null;
  LongTermCareNursingCareCoveragePercentage: number | null;
  InsurancePremiumEndYear: string | null;
  HealthPlanType: HealthPlanTypeEnum | null;
  HealthIndividualDeductible: number | null;
  HealthFamilyDeductiblePerPerson: number | null;
  HealthCoinsurance: number | null;
  HealthCopayment: number | null;
  LifeInsurancePolicyType: LifeInsurancePolicyTypeEnum | null;
  CustomAssetBucketIndex: number | null;
  LifeInsurancePermanentType: LifeInsurancePermanentTypeEnum | null;
  LifeInsuranceGrowthRate: number | null;
  LifeInsurancePolicyTerm: string | null;
  LifeInsurancePolicyStartYear: number | null;
  LifeInsuredMember: string | null;
  IsLinked: boolean;
  IsIntegrated: boolean;
  ShowAccount: boolean;
  SyncAccountValueEnabled: boolean;
  IncludeInGrossEstate: boolean;
  PropertyAndCasualtyInsuranceDetails: PropertyAndCasualtyDTO | null;
  IsOrion: boolean;
  OrionManagementStyleId: number | null;
  HasAdvisorChangedBrokerOfRecord: boolean;
  OwnerModel: OwnerModelDTO[];
}

export enum OwnerTypeDTO {
  Principal = 0,
  Spouse = 1,
  Joint = 2,
  Trust = 3,
  Business = 4,
}

export interface OwnerOptionDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
}

export interface OwnerOptionsDTO {
  People: OwnerOptionDTO[];
  Trusts: OwnerOptionDTO[] | null;
  Businesses: OwnerOptionDTO[] | null;
}

interface AvailableAssetBucketsDTO {
  Title: string;
  ManagementStyles: number[];
  Description: string;
  AssetTypes: number[];
}

export interface InsuranceDTO {
  Items: Record<keyof typeof InsuranceType, InsuranceAccountDTO[]>;
  Options: {
    InsuranceTypeOptions: { label: string; value: InsuranceType }[];
    DisabilityInsurancePolicyTypeOptions: { label: string; value: DisabilityInsurancePolicyType }[];
    InsuranceOwnerOptions: OwnerOptionsDTO;
    OwnerOptions: OwnerOptionsDTO;
    InsuranceCoverageTypeOptions: { label: string; value: InsuranceCoverageType }[];
    DisabilityCoveragePeriodTypeOptions: { label: string; value: InsurancePeriodType }[];
    DisabilityEliminationPeriodTypeOptions: { label: string; value: InsurancePeriodType }[];
    LongTermCareCoveragePeriodTypeOptions: { label: string; value: InsurancePeriodType }[];
    LongTermCareEliminationPeriodTypeOptions: { label: string; value: InsurancePeriodType }[];
    InsurancePremiumEndYearOptions: string[];
    HealthInsuredMembersOptions: { label: string; value: string }[];
    HealthInsurancePlanTypeOptions: { label: string; value: HealthPlanTypeEnum }[];
    LifeInsurancePolicyTypeOptions: { label: string; value: LifeInsurancePolicyTypeEnum }[];
    AllowAssetBucketingCustomization: boolean;
    DefaultAssetBucketIndex: number | null;
    LifeInsurancePermanentTypeOptions: { label: string; value: LifeInsurancePermanentTypeEnum }[];
    LifeInsuranceTermOptions: string[];
    LifeInsurancePurchaseYearOptions: number[];
    LifeInsuranceInsuredMemberOptions: string[];
    InsuranceCoverageStartYearOptions: number[];
    InsuranceCoverageEndYearOptions: number[];
    AvailableAssetBuckets: AvailableAssetBucketsDTO[];
  };
}

interface FetchParams {
  clientId: string;
}

export const fetchInsurance = async ({ clientId }: FetchParams): Promise<InsuranceDTO> => {
  return authFetch(urlWithParams('/Consumer/Insurance/GetInsuranceAccounts', { clientId }));
};

interface QueryParams {
  clientId: string;
}

export const useInsuranceQuery = ({ clientId }: QueryParams) => {
  return useQuery(
    queryKeys.insurance(clientId),

    () => fetchInsurance({ clientId }),

    {
      cacheTime: 0,
    },
  );
};
